/**
 * UrbanEntityFilters — Filtros del panel de inventario urbano
 *
 * @remarks
 * Chips por entityType + selector de status + toggle de visibilidad de capa.
 * Lee y escribe en el urban-inventory store.
 *
 * @module urban-inventory/components
 */

import { Eye, EyeOff } from 'lucide-react';
import { useUrbanInventoryStore } from '../store/urban-inventory-store';
import type { UrbanEntityStatus } from '../types/urban-entity.types';
import { colorForType } from '../utils/urban-entity-colors';

interface UrbanEntityFiltersProps {
  /** Tipos de entidad detectados en los datos cargados */
  detectedEntityTypes: string[];
}

const STATUS_OPTIONS: Array<{ value: UrbanEntityStatus | ''; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'pending_review', label: 'En revisión' },
  { value: 'removed', label: 'Removido' },
];

const TYPE_LABELS: Record<string, string> = {
  tree: 'Árbol',
  streetlight: 'Luminaria',
  waste_container: 'Contenedor',
  transit_stop: 'Parada',
  bench: 'Banco',
  traffic_light: 'Semáforo',
  fountain: 'Fuente',
  waste_depot: 'Depósito',
  waste_hotspot: 'Microbasural',
  building_centroid: 'Edificio',
};

function labelForType(entityType: string): string {
  return TYPE_LABELS[entityType] ?? entityType;
}

export function UrbanEntityFilters({ detectedEntityTypes }: UrbanEntityFiltersProps): JSX.Element {
  const activeEntityTypes = useUrbanInventoryStore((s) => s.activeEntityTypes);
  const statusFilter = useUrbanInventoryStore((s) => s.statusFilter);
  const layerVisible = useUrbanInventoryStore((s) => s.layerVisible);
  const toggleEntityType = useUrbanInventoryStore((s) => s.toggleEntityType);
  const setStatusFilter = useUrbanInventoryStore((s) => s.setStatusFilter);
  const setLayerVisible = useUrbanInventoryStore((s) => s.setLayerVisible);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px 12px' }}>
      {/* Toggle de visibilidad de capa */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <button
          type="button"
          onClick={() => setLayerVisible(!layerVisible)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            fontSize: '11px',
            border: '1px solid var(--color-border, #e2e8f0)',
            borderRadius: '4px',
            background: layerVisible ? 'var(--color-accent-subtle, #eff6ff)' : 'transparent',
            cursor: 'pointer',
            color: 'var(--color-text-secondary, #64748b)',
          }}
        >
          {layerVisible ? <Eye size={12} /> : <EyeOff size={12} />}
          <span>{layerVisible ? 'Visible' : 'Oculto'}</span>
        </button>
      </div>

      {/* Filtro de status */}
      <div>
        <label
          style={{ fontSize: '10px', color: 'var(--color-text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          Estado
        </label>
        <select
          value={statusFilter ?? ''}
          onChange={(e) => {
            const val = e.target.value as UrbanEntityStatus | '';
            setStatusFilter(val === '' ? null : val);
          }}
          style={{
            display: 'block',
            width: '100%',
            marginTop: '2px',
            padding: '3px 6px',
            fontSize: '12px',
            border: '1px solid var(--color-border, #e2e8f0)',
            borderRadius: '4px',
            background: 'var(--color-bg-input, #fff)',
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Chips por entityType */}
      {detectedEntityTypes.length > 0 && (
        <div>
          <label
            style={{ fontSize: '10px', color: 'var(--color-text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            Tipo de entidad
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
            {detectedEntityTypes.map((type) => {
              const active = activeEntityTypes.includes(type);
              const color = colorForType(type);
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleEntityType(type)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    borderRadius: '12px',
                    border: `1px solid ${color}`,
                    background: active ? color : 'transparent',
                    color: active ? '#fff' : color,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: active ? '#fff' : color,
                      flexShrink: 0,
                    }}
                  />
                  {labelForType(type)}
                </button>
              );
            })}
            {activeEntityTypes.length > 0 && (
              <button
                type="button"
                onClick={() => useUrbanInventoryStore.getState().setActiveEntityTypes([])}
                style={{
                  padding: '2px 8px',
                  fontSize: '11px',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border, #e2e8f0)',
                  background: 'transparent',
                  color: 'var(--color-text-muted, #94a3b8)',
                  cursor: 'pointer',
                }}
              >
                Ver todos
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
